import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

export function Modal({ isOpen, onClose, title, children, size = 'lg' }: ModalProps) {
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-2xl',
    lg: 'max-w-4xl',
    xl: 'max-w-6xl',
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center max-md:items-end">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm dark:bg-black/70"
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className={`relative z-10 w-full ${sizeClasses[size]} mx-4 max-h-[90vh] overflow-hidden rounded-xl max-md:mx-0 max-md:max-h-[92dvh] max-md:rounded-b-none max-md:pb-[env(safe-area-inset-bottom)] border border-border bg-bg-elevated shadow-2xl ring-1 ring-black/5 dark:ring-white/10`}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-6 py-4 max-md:px-4">
          <h2 className="text-xl font-semibold text-text-title">{title}</h2>
          <button
            onClick={onClose}
            className="rounded-lg p-1 text-text-secondary transition-colors hover:bg-muted hover:text-text-title"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="max-h-[calc(90vh-80px)] overflow-y-auto max-md:max-h-[calc(92dvh-72px)]">
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
}
